import type { ProviderImportProfile } from '@timelyapp/integration-cloud-api-spec';
import { Box, Button, Card, Pill, Switch, Text } from '@timelyapp/react';
import React from 'react';
import type { GetSubscribableResourcesApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { getResourceContextLabel } from '../../utils/util.profileLabels';

type SubscribableResource = GetSubscribableResourcesApiResponse['subscriptions'][0];

export const SubscriptionResourceCard: React.FC<{
  resource: SubscribableResource;
  onToggle: (resource: SubscribableResource) => void;
  onManageSubscriptions?: (resource: SubscribableResource) => void;
  activeProfile?: ProviderImportProfile;
}> = React.memo(({ resource, onToggle, onManageSubscriptions, activeProfile }) => {
  const numEnabledSubs = resource.sub_subscriptions?.filter(sub => sub.enabled).length ?? 0;
  const domId = `resource-${resource.subscription_id}`;
  const contextLabel = getResourceContextLabel(resource, activeProfile);

  return (
    <Card
      size="small"
      interactive={false}
      flexDirection="row"
      gap="large"
      alignItems="center"
      minHeight={50}
    >
      <Box.Row gap="medium" flexGrow={1}>
        <Switch
          id={domId}
          size="small"
          checked={resource.enabled}
          onChangeChecked={() => onToggle(resource)}
          data-testid={domId}
        />

        <Text.Label htmlFor={domId}>{resource.title}</Text.Label>
        {contextLabel && (
          <Text.Label htmlFor={domId} color="subtle" size="xsmall">
            {contextLabel}
          </Text.Label>
        )}
      </Box.Row>
      <Box.Spacer />
      {resource.enabled && (
        <>
          <Pill size="small" variant={numEnabledSubs > 0 ? 'success' : 'subtle'}>
            {numEnabledSubs}/{resource.sub_subscriptions_total_count ?? 0}
          </Pill>

          <Button
            size="xsmall"
            priority="medium"
            disabled={!resource.enabled}
            onClick={() => onManageSubscriptions?.(resource)}
          >
            Configure
          </Button>
        </>
      )}
    </Card>
  );
});

SubscriptionResourceCard.displayName = 'SubscriptionResourceCard';
